import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  PillBottle,
  Plus,
  Minus,
  History,
  Edit3,
  Trash2,
  AlertTriangle,
  CalendarOff,
  Clock4,
  PackageX,
  ArrowUpRight,
  ArrowDownRight,
  RotateCcw,
  Settings as SettingsIcon,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Select } from '@/components/common/Select'
import { Textarea } from '@/components/common/Textarea'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { Loader, EmptyState } from '@/components/common/Loader'
import { drugApi } from '@/api/drugApi'
import { drugUpdateSchema, stockAdjustSchema } from '@/lib/validators'
import {
  DRUG_CATEGORY_OPTIONS,
  DRUG_CATEGORY_LABEL,
  STOCK_MOVEMENT_LABEL,
  STOCK_MOVEMENT_TONE,
} from '@/lib/constants'
import { useDrugStore } from '@/store/drugStore'
import { cn, formatDate, formatRupees, daysFromToday, timeAgo, cleanPayload } from '@/lib/utils'

const movementIcon = {
  in: <ArrowDownRight className="h-3.5 w-3.5" />,
  out: <ArrowUpRight className="h-3.5 w-3.5" />,
  restore: <RotateCcw className="h-3.5 w-3.5" />,
  adjust: <SettingsIcon className="h-3.5 w-3.5" />,
}

function StockStatePills({ drug }) {
  const pills = []
  if (drug.is_expired) pills.push({ tone: 'danger', icon: <CalendarOff className="h-3 w-3" />, label: 'Expired' })
  else if (drug.is_near_expiry)
    pills.push({ tone: 'accent', icon: <Clock4 className="h-3 w-3" />, label: `${drug.days_to_expiry}d to expiry` })
  if (drug.stock_count === 0) pills.push({ tone: 'danger', icon: <PackageX className="h-3 w-3" />, label: 'Out of stock' })
  else if (drug.is_low_stock) pills.push({ tone: 'accent', icon: <AlertTriangle className="h-3 w-3" />, label: 'Low stock' })
  if (drug.is_slow_moving) pills.push({ tone: 'neutral', label: 'Slow moving' })
  if (drug.is_active === false) pills.push({ tone: 'muted', label: 'Inactive' })
  if (pills.length === 0) pills.push({ tone: 'success', label: 'Healthy' })
  return (
    <div className="flex flex-wrap gap-1.5">
      {pills.map((p, i) => (
        <Badge key={i} tone={p.tone} leftIcon={p.icon}>
          {p.label}
        </Badge>
      ))}
    </div>
  )
}

export default function DrugDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const upsert = useDrugStore((s) => s.upsert)
  const removeFromStore = useDrugStore((s) => s.remove)

  const [drug, setDrug] = useState(null)
  const [movements, setMovements] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null) // 'not_found' | 'backend' | null

  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjustMode, setAdjustMode] = useState('in') // 'in' | 'out'
  const [adjustData, setAdjustData] = useState({ delta: '', note: '' })
  const [adjustErrors, setAdjustErrors] = useState({})
  const [adjusting, setAdjusting] = useState(false)

  const [editOpen, setEditOpen] = useState(false)
  const [editData, setEditData] = useState({})
  const [editErrors, setEditErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const load = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [d, m] = await Promise.all([
        drugApi.get(id),
        drugApi.listMovements(id, { limit: 50, silent: true }).catch(() => []),
      ])
      setDrug(d)
      setMovements(Array.isArray(m) ? m : [])
    } catch (err) {
      const status = err?.response?.status
      setLoadError(status === 404 ? 'not_found' : 'backend')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  /* ---------- stock adjust ---------- */
  const openAdjust = (mode) => {
    setAdjustMode(mode)
    setAdjustData({ delta: '', note: '' })
    setAdjustErrors({})
    setAdjustOpen(true)
  }

  const handleAdjust = async () => {
    const signedDelta =
      adjustMode === 'in'
        ? Math.abs(Number(adjustData.delta) || 0)
        : -Math.abs(Number(adjustData.delta) || 0)
    const parsed = stockAdjustSchema.safeParse({ delta: signedDelta, note: adjustData.note })
    if (!parsed.success) {
      const errs = {}
      parsed.error.issues.forEach((iss) => {
        errs[iss.path[0]] = iss.message
      })
      setAdjustErrors(errs)
      return
    }
    if (adjustMode === 'out' && drug && Math.abs(signedDelta) > drug.stock_count) {
      setAdjustErrors({ delta: `Only ${drug.stock_count} in stock` })
      return
    }
    setAdjusting(true)
    try {
      const updated = await drugApi.adjustStock(id, parsed.data)
      setDrug(updated)
      upsert(updated)
      const mvts = await drugApi.listMovements(id, { limit: 50 })
      setMovements(mvts)
      toast.success(
        adjustMode === 'in' ? 'Stock added' : 'Stock removed',
      )
      setAdjustOpen(false)
    } catch {
      // toast handled by axios interceptor
    } finally {
      setAdjusting(false)
    }
  }

  /* ---------- edit metadata ---------- */
  const openEdit = () => {
    setEditData({
      name: drug.name || '',
      category: drug.category_id || '',
      dosage: drug.dosage || '',
      reorder_level: drug.reorder_level ?? '',
      mfg_date: drug.mfg_date || '',
      exp_date: drug.exp_date || '',
      mrp: drug.mrp ?? '',
      is_active: drug.is_active,
    })
    setEditErrors({})
    setEditOpen(true)
  }

  const handleEdit = async () => {
    const parsed = drugUpdateSchema.safeParse(editData)
    if (!parsed.success) {
      const errs = {}
      parsed.error.issues.forEach((iss) => {
        errs[iss.path[0]] = iss.message
      })
      setEditErrors(errs)
      return
    }
    // Only send changed fields
    const patch = {}
    Object.entries(parsed.data).forEach(([k, v]) => {
      if (v === undefined) return
      const currentKey = k === 'category' ? 'category_id' : k
      const currentVal = drug[currentKey]
      const normalised = v === '' ? null : v
      const currentNormalised = currentVal ?? null
      if (normalised !== currentNormalised) {
        patch[k] = v === '' ? null : v
      }
    })
    if (Object.keys(patch).length === 0) {
      toast('No changes to save')
      setEditOpen(false)
      return
    }
    const cleaned = cleanPayload(patch) || patch
    setSaving(true)
    try {
      const updated = await drugApi.update(id, cleaned)
      setDrug(updated)
      upsert(updated)
      toast.success('Medicine updated')
      setEditOpen(false)
    } catch {
      // toast handled
    } finally {
      setSaving(false)
    }
  }

  /* ---------- soft delete ---------- */
  const handleDelete = async () => {
    setDeleting(true)
    try {
      await drugApi.remove(id)
      removeFromStore(id)
      toast.success(`${drug.name} deactivated`)
      navigate('/drugs')
    } catch {
      setDeleting(false)
    }
  }

  const d2e = useMemo(() => (drug ? daysFromToday(drug.exp_date) : null), [drug])

  if (loading) {
    return (
      <div className="container-page py-6 sm:py-8">
        <Loader label="Loading medicine…" />
      </div>
    )
  }

  if (!drug) {
    const isBackend = loadError === 'backend'
    return (
      <div className="container-page py-6 sm:py-8">
        <EmptyState
          icon={<PillBottle className="h-6 w-6" />}
          title={isBackend ? "Couldn't load medicine" : 'Medicine not found'}
          description={
            isBackend
              ? 'The backend ran into an error. Please try again in a moment.'
              : 'The drug may have been deleted, or the ID is wrong.'
          }
          action={
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              {isBackend && (
                <Button onClick={load} leftIcon={<ArrowLeft className="h-4 w-4" />}>
                  Retry
                </Button>
              )}
              <Button
                variant={isBackend ? 'secondary' : 'primary'}
                onClick={() => navigate('/drugs')}
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to drug list
              </Button>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Phase 6 — Inventory"
        title={drug.name}
        description={
          <span className="font-mono text-xs">{drug.drug_id}</span>
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate('/drugs')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(true)}
              leftIcon={<Trash2 className="h-4 w-4" />}
              className="text-rose-600 hover:bg-rose-50"
            >
              Deactivate
            </Button>
            <Button variant="secondary" onClick={openEdit} leftIcon={<Edit3 className="h-4 w-4" />}>
              Edit details
            </Button>
          </>
        }
      />

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.1fr_1fr]">
        {/* Left — info card + stock controls */}
        <div className="space-y-5">
          <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="label-text">On-hand stock</p>
                <p
                  className={cn(
                    'mt-1 text-5xl font-bold tabular-nums',
                    drug.stock_count === 0
                      ? 'text-rose-600'
                      : drug.is_low_stock
                      ? 'text-accent-700'
                      : 'text-primary-800',
                  )}
                >
                  {drug.stock_count}
                </p>
                <p className="mt-1 text-xs text-ink-500">
                  Reorder at {drug.reorder_level ?? '—'}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button onClick={() => openAdjust('in')} leftIcon={<Plus className="h-4 w-4" />}>
                  Restock
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openAdjust('out')}
                  leftIcon={<Minus className="h-4 w-4" />}
                  disabled={drug.stock_count === 0}
                >
                  Write-off
                </Button>
              </div>
            </div>

            <div className="mt-4">
              <StockStatePills drug={drug} />
            </div>

            <dl className="mt-5 grid gap-3 border-t border-ink-100 pt-5 text-sm sm:grid-cols-2">
              <div>
                <dt className="label-text">Category</dt>
                <dd className="mt-0.5 font-semibold text-ink-900">
                  {DRUG_CATEGORY_LABEL[drug.category_id] || drug.category_id}
                </dd>
              </div>
              <div>
                <dt className="label-text">Dosage</dt>
                <dd className="mt-0.5 font-semibold text-ink-900">{drug.dosage || '—'}</dd>
              </div>
              <div>
                <dt className="label-text">MRP per unit</dt>
                <dd className="mt-0.5 font-semibold text-ink-900 tabular-nums">
                  {formatRupees(drug.mrp)}
                </dd>
              </div>
              <div>
                <dt className="label-text">Reorder level</dt>
                <dd className="mt-0.5 font-semibold text-ink-900 tabular-nums">
                  {drug.reorder_level ?? 'Default'}
                </dd>
              </div>
              <div>
                <dt className="label-text">Mfg date</dt>
                <dd className="mt-0.5 text-ink-800">{formatDate(drug.mfg_date)}</dd>
              </div>
              <div>
                <dt className="label-text">Expiry date</dt>
                <dd className="mt-0.5 text-ink-800">
                  {formatDate(drug.exp_date)}{' '}
                  {d2e !== null && (
                    <span
                      className={cn(
                        'text-xs',
                        d2e < 0 ? 'text-rose-600 font-semibold' : d2e < 90 ? 'text-accent-700' : 'text-ink-500',
                      )}
                    >
                      ({d2e < 0 ? `${Math.abs(d2e)}d ago` : `in ${d2e}d`})
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="label-text">Last dispensed</dt>
                <dd className="mt-0.5 text-ink-800">
                  {drug.last_dispensed_at ? timeAgo(drug.last_dispensed_at) : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="label-text">Added on</dt>
                <dd className="mt-0.5 text-ink-800">{formatDate(drug.created_at)}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Right — movements timeline */}
        <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-ink-500" />
              <p className="font-semibold text-ink-900">Stock movements</p>
            </div>
            <Badge tone="muted">{movements.length}</Badge>
          </div>
          {movements.length === 0 ? (
            <p className="mt-6 text-center text-sm text-ink-500">No movements yet.</p>
          ) : (
            <ul className="mt-4 space-y-2 max-h-[520px] overflow-y-auto no-scrollbar pr-1">
              {movements.map((m) => (
                <li
                  key={m.id}
                  className="flex items-start gap-3 rounded-xl bg-cream/60 p-3 ring-1 ring-ink-100"
                >
                  <Badge tone={STOCK_MOVEMENT_TONE[m.change_type] || 'neutral'} leftIcon={movementIcon[m.change_type]}>
                    {STOCK_MOVEMENT_LABEL[m.change_type] || m.change_type}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink-900 tabular-nums">
                      {m.change_type === 'out' ? '−' : '+'}
                      {m.quantity}
                    </p>
                    {m.note && (
                      <p className="mt-0.5 text-xs text-ink-600">{m.note}</p>
                    )}
                    {m.bill_id && (
                      <p className="mt-0.5 text-[11px] font-mono text-ink-500">
                        Bill {m.bill_id}
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-[11px] text-ink-500">{timeAgo(m.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ----------------- Stock adjust modal ----------------- */}
      <Modal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        title={adjustMode === 'in' ? 'Restock medicine' : 'Write-off / correction'}
        description={
          adjustMode === 'in'
            ? `Add stock to ${drug.name}. This is logged as an "in" movement.`
            : `Remove stock from ${drug.name}. Logged as an "adjust" movement.`
        }
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAdjust}
              loading={adjusting}
              variant={adjustMode === 'in' ? 'primary' : 'accent'}
            >
              {adjustMode === 'in' ? 'Add stock' : 'Remove stock'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label={adjustMode === 'in' ? 'Quantity to add' : 'Quantity to remove'}
            required
            type="number"
            min="1"
            value={adjustData.delta}
            onChange={(e) => setAdjustData((d) => ({ ...d, delta: e.target.value }))}
            placeholder="0"
            error={adjustErrors.delta}
            hint={
              adjustMode === 'out'
                ? `Currently ${drug.stock_count} in stock`
                : undefined
            }
          />
          <Textarea
            label="Note (optional)"
            rows={2}
            value={adjustData.note}
            onChange={(e) => setAdjustData((d) => ({ ...d, note: e.target.value }))}
            placeholder={
              adjustMode === 'in'
                ? 'e.g. New supplier batch, invoice #1234'
                : 'e.g. Damaged tablets, count correction'
            }
            error={adjustErrors.note}
          />
        </div>
      </Modal>

      {/* ----------------- Edit modal ----------------- */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit medicine details"
        description="Metadata only. Stock changes must use the Restock / Write-off buttons so the audit trail stays clean."
        footer={
          <>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} loading={saving}>
              Save changes
            </Button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Name"
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            error={editErrors.name}
            className="sm:col-span-2"
          />
          <Select
            label="Category"
            value={editData.category}
            onChange={(e) => setEditData({ ...editData, category: e.target.value })}
            options={DRUG_CATEGORY_OPTIONS}
            error={editErrors.category}
          />
          <Input
            label="Dosage"
            value={editData.dosage}
            onChange={(e) => setEditData({ ...editData, dosage: e.target.value })}
            error={editErrors.dosage}
          />
          <Input
            label="Reorder level"
            type="number"
            min="0"
            value={editData.reorder_level}
            onChange={(e) => setEditData({ ...editData, reorder_level: e.target.value })}
            error={editErrors.reorder_level}
          />
          <Input
            label="MRP (₹)"
            type="number"
            min="0"
            step="0.01"
            value={editData.mrp}
            onChange={(e) => setEditData({ ...editData, mrp: e.target.value })}
            error={editErrors.mrp}
          />
          <Input
            label="Mfg date"
            type="date"
            value={editData.mfg_date}
            onChange={(e) => setEditData({ ...editData, mfg_date: e.target.value })}
            error={editErrors.mfg_date}
          />
          <Input
            label="Expiry date"
            type="date"
            value={editData.exp_date}
            onChange={(e) => setEditData({ ...editData, exp_date: e.target.value })}
            error={editErrors.exp_date}
          />
        </div>
      </Modal>

      {/* ----------------- Deactivate modal ----------------- */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Deactivate medicine?"
        description="The medicine will be hidden from the active list but historical bills will still show it."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Keep active
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              Deactivate
            </Button>
          </>
        }
      />
    </div>
  )
}
